import React, { useRef, useState, useCallback, useLayoutEffect } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import uniqueId from 'lodash.uniqueid';
import { TruncatedCell } from '../../../styles/common';

/**
 * A table cell that ellipsises overflowing content and discloses the full text
 * through the portal tooltip component.
 *
 * PM-18391 / PM-18386 require the disclosure to work "on hover and keyboard
 * focus" and to use the portal tooltip rather than a native `title` attribute.
 * The previous implementation was a CSS `:hover` overflow-expand, which is
 * unreachable by keyboard (WCAG 1.4.13, 2.1.1).
 *
 * PM-21936: that first implementation drove the tooltip from two places at once.
 * It kept its own `isOpen` state from onMouseEnter/onMouseLeave, mounted the
 * tooltip only while open, and passed `toggle={close}`. But reactstrap attaches
 * its own mouseover/focusin listeners to the target and asks to open by calling
 * `toggle` - which here only ever closed. So hovering ran open -> mount ->
 * reactstrap asks to open -> close -> unmount -> still hovering -> open, and the
 * cell blinked continuously. The click that should select the row was lost in the
 * churn, because mousedown and mouseup landed on different renders.
 *
 * The open state now belongs to reactstrap alone. The tooltip is rendered for as
 * long as the cell is truncated and manages itself; there is no second driver to
 * disagree with. Mounting one per truncated cell is cheap: reactstrap renders
 * null while closed, so a closed tooltip is a pair of listeners and no DOM.
 */
const TruncatedTableCell = ({ children }) => {
    const cellRef = useRef(null);
    const tooltipId = useRef(uniqueId('truncated-cell-')).current;
    const [isTruncated, setIsTruncated] = useState(false);
    const [fullText, setFullText] = useState('');

    const measure = useCallback(() => {
        const el = cellRef.current;
        if (!el) return;
        // +1 absorbs sub-pixel rounding, which would otherwise report a
        // perfectly-fitting cell as truncated.
        setIsTruncated(el.scrollWidth > el.clientWidth + 1);
        // Read once here rather than during render: the tooltip needs the text the
        // cell actually holds, and a ref cannot be read safely while rendering.
        setFullText(el.textContent || '');
    }, []);

    useLayoutEffect(() => {
        measure();

        // Columns are resizable, so whether a cell overflows changes without its
        // content changing. The old code re-measured from its hover handler; with
        // the handlers gone, the element itself has to report its own resizes or a
        // narrowed column would keep its stale "fits" verdict and disclose nothing.
        const el = cellRef.current;
        if (!el || typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [measure, children]);

    return (
        <>
            <TruncatedCell
                ref={cellRef}
                id={tooltipId}
                tabIndex={isTruncated ? 0 : undefined}
            >
                {children}
            </TruncatedCell>
            {isTruncated && (
                // "hover focus" is reactstrap's own default for Tooltip and is stated
                // here so the keyboard half of the requirement cannot be lost to a
                // change of default. Notably absent is "click", which would fight the
                // row-selection click this cell sits on top of.
                <UncontrolledTooltip target={tooltipId} trigger="hover focus">
                    {fullText}
                </UncontrolledTooltip>
            )}
        </>
    );
};

export default TruncatedTableCell;
